import React, { useEffect, useState } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Form, 
  Input, 
  Button, 
  Typography, 
  Space, 
  Spin, 
  Alert,
  Divider,
  Avatar
} from 'antd';
import { 
  UserOutlined, 
  EditOutlined, 
  SaveOutlined, 
  VideoCameraOutlined,
  HeartOutlined,
  HistoryOutlined,
  GlobalOutlined,
  LinkOutlined,
  SettingOutlined,
  LogoutOutlined
} from '@ant-design/icons';
import Setting from '../Setting';
import { UserAPI } from '../backend/api';
import { useAuth } from '../utils/auth';

const { Title, Paragraph } = Typography;

const ProfilePage = () => {
  const { user, logout } = useAuth();
  const [profile, setProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    if (user) {
      fetchProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchProfile = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await UserAPI.getProfile();
      setProfile(response.data);
      form.setFieldsValue(response.data);
    } catch (err) {
      setError('Failed to fetch profile');
      console.error('Profile fetch error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    try {
      await UserAPI.updateProfile(values);
      Setting.showMessage('success', 'Profile updated successfully!');
      setIsEditing(false);
      fetchProfile();
    } catch (error) {
      Setting.showMessage('error', 'Failed to update profile');
    }
  };

  const handleLogout = async () => {
    await logout();
    Setting.showMessage('success', 'Logged out successfully');
  };

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error"
        description={error}
        type="error"
        showIcon
        style={{ margin: '20px' }}
      />
    );
  }

  const renderAvatar = () => (
    <div style={{ textAlign: 'center', marginBottom: '20px' }}>
      <Avatar 
        src={profile.avatar} 
        size={150} 
        style={{ 
          backgroundColor: '#1890ff', 
          fontSize: '48px',
          marginBottom: '10px'
        }}
      >
        {profile.name?.charAt(0).toUpperCase()}
      </Avatar>
      <Title level={3}>{profile.name}</Title>
      <Paragraph>
        {profile.email}
      </Paragraph>
    </div>
  );

  const renderProfileInfo = () => (
    <div>
      <Divider orientation="left">Account Information</Divider>
      <Space direction="vertical" style={{ width: '100%' }}>
        <Form.Item label="Username" labelCol={{ span: 6 }} wrapperCol={{ span: 18 }}>
          <Input 
            prefix={<UserOutlined />} 
            value={profile.name}
            disabled
          />
        </Form.Item>
        <Form.Item label="Email" labelCol={{ span: 6 }} wrapperCol={{ span: 18 }}>
          <Input 
            prefix={<VideoCameraOutlined />} 
            value={profile.email}
            disabled
          />
        </Form.Item>
        <Form.Item label="Member Since" labelCol={{ span: 6 }} wrapperCol={{ span: 18 }}>
          <Input 
            prefix={<HistoryOutlined />} 
            value={new Date(profile.createdAt).toLocaleDateString()}
            disabled
          />
        </Form.Item>
      </Space>
    </div>
  );

  const renderEditProfileForm = () => (
    <div>
      <Divider orientation="left">Edit Profile</Divider>
      <Form
        form={form}
        onFinish={handleSubmit}
        layout="vertical"
      >
        <Form.Item
          name="displayName"
          label="Display Name"
          labelCol={{ span: 6 }}
          wrapperCol={{ span: 18 }}
          initialValue={profile.displayName}
          rules={[{ required: true, message: 'Please enter your display name' }]}
        >
          <Input prefix={<EditOutlined />} placeholder="Display Name" />
        </Form.Item>

        <Form.Item
          name="bio"
          label="Bio"
          labelCol={{ span: 6 }}
          wrapperCol={{ span: 18 }}
          initialValue={profile.bio}
        >
          <Input.TextArea rows={3} placeholder="Tell us about yourself" />
        </Form.Item>

        <Form.Item
          name="location"
          label="Location"
          labelCol={{ span: 6 }}
          wrapperCol={{ span: 18 }}
          initialValue={profile.location}
        >
          <Input prefix={<GlobalOutlined />} placeholder="City, Country" />
        </Form.Item>

        <Form.Item
          name="website"
          label="Website"
          labelCol={{ span: 6 }}
          wrapperCol={{ span: 18 }}
          initialValue={profile.website}
        >
          <Input prefix={<LinkOutlined />} placeholder="https://yourwebsite.com" />
        </Form.Item>

        <Form.Item wrapperCol={{ offset: 6 }}>
          <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
            Save Changes
          </Button>
          <Button onClick={() => setIsEditing(false)} style={{ marginLeft: '10px' }}>
            Cancel
          </Button>
        </Form.Item>
      </Form>
    </div>
  );

  const renderUserActions = () => (
    <Card style={{ marginTop: '30px' }}>
      <Button 
        type="primary" 
        icon={<SettingOutlined />} 
        style={{ width: '100%', marginBottom: '10px' }}
      >
        Account Settings
      </Button>
      <Button 
        type="primary" 
        danger 
        icon={<LogoutOutlined />} 
        style={{ width: '100%' }}
        onClick={handleLogout}
      >
        Sign Out
      </Button>
    </Card>
  );

  const renderWatchlist = () => (
    <Card title={<><HeartOutlined /> Watchlist</>} style={{ marginTop: '30px' }}>
      <p>No movies in your watchlist yet.</p>
    </Card>
  );

  return (
    <div>
      <Title level={2}>My Profile</Title>
      
      <Row gutter={[24, 24]}>
        <Col xs={24} sm={24} md={8}>
          {renderAvatar()}
          {renderUserActions()}
        </Col>
        
        <Col xs={24} sm={24} md={16}>
          {isEditing ? (
            renderEditProfileForm()
          ) : (
            <>
              {renderProfileInfo()}
              <Button type="primary" onClick={() => setIsEditing(true)} style={{ marginBottom: '20px' }}>
                <EditOutlined /> Edit Profile
              </Button>
            </>
          )}
          
          {renderWatchlist()}
        </Col>
      </Row>
    </div>
  );
};

export default ProfilePage;